import clsx from 'clsx'
import { useRouter } from 'next/router'
import React, { FC, useContext, useState } from 'react'
import styled from 'styled-components'

import { YOLOFlagIcon } from '@Components/Icons'
import { ShippingInfoModal } from '@Components/Shipping/ShippingInfoModal'
import { MetadataContext } from '@Globals/Metadata'
import { formatMoney } from '@Globals/Money'
import { down, up } from '@Globals/Style/breakpoints'
import { resetButton, sectionInner, sectionOuter } from '@Globals/Style/mixins'
import { useTranslation } from '@Globals/Translations'
import { UserContext } from '@Globals/User'

export interface USPBarProps {
  evenSpacingOnSmallDesktop?: boolean
  smallTextBelowPhablet?: boolean
  smallTextOnSmallDesktop?: boolean
}

interface WrapperProps {
  color?: string
  textColor?: string
}

const Wrapper = styled.div<WrapperProps>`
  ${sectionOuter};
  background-color: ${({ theme }) => theme.colors.greyDark};

  .inner {
    position: relative;
    ${sectionInner};
    height: 30px;
  }

  .item {
    position: absolute;
    top: 0;
    width: 50%;
    display: flex;
    align-items: center;
    height: 100%;

    a, span, strong {
      font-size: 11px;
      color: ${({ textColor, theme }) => textColor ?? theme.colors.white};

      ${up('phablet')} {
        font-size: 12px;
      }
    }

    strong {
      font-weight: normal;

      &::after {
        content: '-';
        display: inline-block;
        font-size: 12px;
        color: ${({ theme }) => theme.colors.white};
        padding-left: 5px;
        padding-right: 1px;
      }
    }

    &.firstItem {
      left: 0;
      z-index: 1;

      ${down('desktop')} {
        display: none;
        padding: 0 5px;
      }
    }

    &.middleItem {
      left: 25%;
      z-index: 3;
      justify-content: center;

      .header-spacing-line {
        padding-left: 5px;
      }

      ${down('desktop')} {
        width: 100%;
        left: 0;
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;

        & .header-description {
          flex: 0 0 auto;
          max-width: fit-content;
        }

        & .seeOptions {
          flex: 0 0 auto;
          padding-left: 0;
        }

        .header-spacing-line {
          padding-right: 5px;
        }
      }
    }

    &.lastItem {
      right: 0;
      z-index: 2;
      justify-content: flex-end;

      ${down('desktop')} {
        display: none;
      }
    }
  }

  .countryFlag {
    margin-right: 8px;
  }

  .seeOptions {
    padding-left: 5px;

    & > button {
      ${resetButton};
      display: inline;
      text-decoration: underline;
    }
  }

  &.smallTextOnSmallDesktop {
    .item {
      a, span, strong {
        font-size: 8px;

        ${up('mobile')} {
          font-size: 9px;
        }

        ${up('phablet')} {
          font-size: 12px;
        }
      }
    }
  }

  &.evenSpacingOnSmallDesktop {
    ${up('desktop')} {
      .inner {
        display: flex;
        justify-content: space-between;
      }

      .item {
        position: relative;
        width: auto;

        &.firstItem {
          left: inherit;
        }

        &.middleItem {
          left: inherit;
        }

        &.lastItem {
          right: inherit;
        }
      }
    }
  }

  &.smallTextBelowPhablet {
    ${down('mobile')} {
      .item {
        a, span, strong {
          font-size: 8px;
        }
      }
    }

    ${down('phablet')} {
      .item {
        a, span, strong {
          font-size: 9px;
        }
      }
    }
  }
`

export const USPBar: FC<USPBarProps> = (
  {
    evenSpacingOnSmallDesktop = false,
    smallTextBelowPhablet = false,
    smallTextOnSmallDesktop = false,
  }: USPBarProps,
) => {
  const { t, tDanger } = useTranslation()
  const { store } = useContext(MetadataContext)
  const { locale = '' } = useRouter()
  const { userData } = useContext(UserContext)
  const YOLOCountry = userData?.otherCountry?.country || ''
  const phone = store?.config?.phone
  const cheapestShippingPrice = store?.config?.cheapestShippingPrice || false
  const freeShippingThreshold = store?.config?.freeShippingThreshold || false
  const [shippingInfoModalOpen, setShippingInfoModalOpen] = useState(false)

  return (
    <>
      <Wrapper className={clsx({
        evenSpacingOnSmallDesktop,
        smallTextBelowPhablet,
        smallTextOnSmallDesktop,
      })}
      >
        <div className="inner">
          {/* Returns */}
          <div className="item firstItem">
            <span {...tDanger('header:RETURN_POLICY_365_DAYS')} />
          </div>

          {/* Delivery */}
          <div className="item middleItem">
            {YOLOCountry && (
              <div className="countryFlag">
                <YOLOFlagIcon country={YOLOCountry} />
              </div>
            )}
            {/* - Check for shipping threshold  */}
            {cheapestShippingPrice && freeShippingThreshold
              ? (
                <span className="header-description">
                  {t('header:SHIPPING_X_FREE_OVER_X', {
                    arg1: formatMoney(store?.id, locale, cheapestShippingPrice),
                    arg2: formatMoney(store?.id, locale, freeShippingThreshold),
                  })}
                </span>
              )
              : (
                <span {...tDanger('header:DESKTOP_HEADER_USP_FREE_DELIVERY')} />
              )}
            <span className="header-spacing-line">-</span>
            <span className="seeOptions">
              <button onClick={() => setShippingInfoModalOpen(true)} type="button">
                {t('header:SEE_OPTIONS')}
              </button>
            </span>
          </div>

          {/* Contact */}
          <div className="item lastItem">
            <span>
              {phone && (
                <a href={`tel:${phone.replace(' ', '')}`}>
                  {phone}
                  {' - '}
                </a>
              )}
              (
              {t('header:PHONE_OPEN_HOURS').toLowerCase()}
              )
            </span>
          </div>
        </div>
      </Wrapper>

      <ShippingInfoModal
        isOpen={shippingInfoModalOpen}
        onHide={() => setShippingInfoModalOpen(false)}
        sourceID="header"
      />
    </>
  )
}
